"use client";

import { getStore } from "@shared/utils.locale";
import { usePathname } from "next/navigation";

import { normalisePathname } from "./normalisePathname";

const query =
  typeof window === "undefined"
    ? {}
    : Object.fromEntries(new URLSearchParams(window.location.search).entries());

export type Router = {
  pathname: string;
  asPath: string;
  query: Record<string, string>;
  isReady: boolean;
  beforeUnload: (fn: EventListener) => void;
  navigateTo: (uri: string) => void;
  setSearchParams: (name: string, value: string) => void;
  reload: () => void;
  removeParamsFromURL: (params: string[]) => void;
};

const Router = ({ pathname }: { pathname: string }) => ({
  pathname,
  asPath: normalisePathname(pathname),
  query,
  isReady: typeof window !== "undefined",
  beforeUnload: (fn: EventListener) =>
    window.addEventListener("beforeunload", fn),
  navigateTo: (uri: string) => {
    const locale = getStore();
    const isAbsolute = uri.startsWith("http");
    const url = locale === "en" ? `/${uri}` : `/${locale}/${uri}`;
    const href = isAbsolute ? uri : url;
    window.location.href = href;
  },
  setSearchParams: (name: string, value: string) => {
    const url = new URL(window.location.href);
    url.searchParams.set(name, value);
    window.history.pushState({}, "", url);
  },
  reload: () => {
    window.location.reload();
  },
  removeParamsFromURL: (params: string[]) => {
    if (params.length === 0) return;

    const urlParams = new URLSearchParams(window.location.search);
    let paramsRemoved = false;

    params.forEach((param) => {
      if (urlParams.has(param)) {
        urlParams.delete(param);
        paramsRemoved = true;
      }
    });

    if (paramsRemoved) {
      const url = new URL(window.location.href);
      url.search = urlParams.toString();
      window.history.replaceState({}, "", url.toString());
    }
  }
});

export const useRouter = (): Router => {
  const pathname = usePathname();
  return Router({ pathname });
};
